import { z } from 'zod';
import { localeSchema, rawPhoneSchema, uuidSchema } from './common.js';
/**
 * Merchant password policy.
 *
 * Length only, no composition rules. NIST 800-63B dropped character-class
 * requirements because they push people toward `Password1!` — predictable to a
 * cracker, hard for a human. Length is what actually helps.
 */
export const passwordSchema = z
    .string()
    .min(12, 'Use at least 12 characters')
    .max(200, 'That is longer than we can store');
export const registerSchema = z.strictObject({
    organizationName: z.string().trim().min(1).max(120),
    fullName: z.string().trim().min(1).max(120),
    email: z.string().trim().toLowerCase().max(254).pipe(z.email()),
    password: passwordSchema,
});
export const loginSchema = z.strictObject({
    email: z.string().trim().toLowerCase().max(254).pipe(z.email()),
    // Deliberately not `passwordSchema`: an existing account may predate a policy
    // change, and rejecting their real password at the parser locks them out.
    password: z.string().min(1).max(200),
});
/**
 * `POST /auth/password/forgot`. Same email normalisation as login, so the
 * address a merchant types here finds the account they sign in with.
 */
export const forgotPasswordSchema = z.strictObject({
    email: z.string().trim().toLowerCase().max(254).pipe(z.email()),
});
/**
 * `POST /auth/password/reset`.
 *
 * The token is the base64url encoding of 32 random bytes — 43 characters. The
 * bounds are loose on purpose: they only stop a megabyte of junk reaching a
 * hash, and "invalid" is decided by the lookup, not the parser. The new
 * password gets the signup policy, unlike login: it is being chosen now.
 */
export const resetPasswordSchema = z.strictObject({
    token: z.string().trim().min(16).max(256),
    newPassword: passwordSchema,
});
/**
 * A 4-digit PIN. Low entropy by design — a barista enters it dozens of times a
 * shift on a shared tablet, and anything longer gets written on a sticky note.
 * The device binding and the attempt lockout carry the security.
 */
export const pinSchema = z.string().regex(/^\d{4}$/, 'The PIN is four digits');
export const staffLoginSchema = z.strictObject({
    staffMemberId: uuidSchema,
    pin: pinSchema,
});
export const deviceRegisterSchema = z.strictObject({
    code: z.string().trim().min(4).max(32),
    label: z.string().trim().min(1).max(80),
});
export const createDeviceCodeSchema = z.strictObject({
    locationId: uuidSchema,
    label: z.string().trim().min(1).max(80),
});
export const requestOtpSchema = z.strictObject({
    phoneE164: rawPhoneSchema,
});
export const verifyOtpSchema = z.strictObject({
    phoneE164: rawPhoneSchema,
    code: z.string().regex(/^\d{6}$/, 'The code is six digits'),
});
/**
 * A birth date, `YYYY-MM-DD` — features/CUSTOMER-CARD.md.
 *
 * "Not in the future" is checked against the latest date anywhere on Earth
 * (UTC+14), so a customer born today is never refused because the server's
 * clock is still on yesterday.
 */
export const birthDateSchema = z.iso
    .date()
    .refine((d) => d >= '1900-01-01', 'That date is too far back.')
    .refine((d) => d <= new Date(Date.now() + 14 * 3_600_000).toISOString().slice(0, 10), 'That date is in the future.');
/**
 * Neither the name nor the birthday can be emptied once set (CARD-3, CARD-4):
 * both may be changed, but `null` and `""` are refused here, at the boundary,
 * so there is no request that clears them. Erasing them is what `DELETE /me`
 * is for.
 */
export const updateProfileSchema = z.strictObject({
    displayName: z.string().trim().min(1).max(120).optional(),
    birthDate: birthDateSchema.optional(),
    locale: localeSchema.optional(),
});
//# sourceMappingURL=auth.js.map